import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, PieChart, Activity, Users, Database, Zap, Shield, ArrowRight, Play, Star,Brain,setHoveredCard,hoveredCard } from 'lucide-react';
import Header from './header';
import Footer from './footer';
const DataAnalyticsPage = () => {
  const [animatedStats, setAnimatedStats] = useState({
    customers: 0,
    accuracy: 0,
    insights: 0,
    growth: 0
  });
  const [hoveredCard, setHoveredCard] = useState(null);

  // Animate stats on component mount
  useEffect(() => {
    const targets = { customers: 2500, accuracy: 98, insights: 15000, growth: 45 };
    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;

    const animate = (step) => {
      const progress = Math.min(step / steps, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedStats({
        customers: Math.floor(targets.customers * easeOut),
        accuracy: Math.floor(targets.accuracy * easeOut),
        insights: Math.floor(targets.insights * easeOut),
        growth: Math.floor(targets.growth * easeOut)
      });

      if (step < steps) {
        setTimeout(() => animate(step + 1), stepTime);
      }
    };

    animate(0);
  }, []);

  const chartData = [
    { icon: <BarChart3 className="w-12 h-12 text-blue-600" />, title: "Revenue Analytics", value: "+34%" },
    { icon: <TrendingUp className="w-12 h-12 text-green-600" />, title: "Growth Metrics", value: "+28%" },
    { icon: <PieChart className="w-12 h-12 text-purple-600" />, title: "Market Share", value: "67%" },
    { icon: <Activity className="w-12 h-12 text-orange-600" />, title: "Live Tracking", value: "Real-time" }
  ];

const features = [
    {
      title: "Machine Learning Models",
      description: "Deploy cutting-edge ML algorithms to predict trends, classify data, and automate decision-making processes with unprecedented accuracy.",
      icon: <Brain className="w-8 h-8 text-white" />,
      gradient: "from-purple-500 to-pink-500",
      hoverGradient: "from-purple-400 to-pink-400"
    },
    {
      title: "Real-time Analytics",
      description: "Process and analyze streaming data in real-time to make instant business decisions and respond to market changes as they happen.",
      icon: <Zap className="w-8 h-8 text-white" />,
      gradient: "from-yellow-500 to-orange-500",
      hoverGradient: "from-yellow-400 to-orange-400"
    },
    {
      title: "Data Visualization",
      description: "Create stunning, interactive dashboards and reports that transform complex data into clear, actionable insights for all stakeholders.",
      icon: <BarChart3 className="w-8 h-8 text-white" />,
      gradient: "from-green-500 to-teal-500",
      hoverGradient: "from-green-400 to-teal-400"
    },
    {
      title: "Predictive Analytics",
      description: "Leverage advanced statistical models and AI to forecast future trends, identify opportunities, and mitigate risks before they impact your business.",
      icon: <TrendingUp className="w-8 h-8 text-white" />,
      gradient: "from-blue-500 to-cyan-500",
      hoverGradient: "from-blue-400 to-cyan-400"
    }
  ];


  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Data Science Director",
      company: "TechCorp",
      rating: 5,
      text: "This platform transformed how we handle data analytics. The insights are incredible and actionable."
    },
    {
      name: "Michael Rodriguez",
      role: "VP of Analytics",
      company: "InnovateLabs",
      rating: 5,
      text: "The real-time capabilities have given us a competitive edge. Our decision-making process is now data-driven."
    },
    {
      name: "Emily Johnson",
      role: "Business Intelligence Lead",
      company: "DataFlow Inc",
      rating: 5,
      text: "User-friendly interface with powerful analytics. Our entire team adopted it within days."
    }
  ];

  const capabilities = [
    "Providing an Experience-Centric approach to all touchpoints, architectures, stakeholders, and implementations",
    "Delivering predictive analytics through machine learning and AI-powered insights",
    "Creating end-to-end data pipelines with automated processing and validation",
    "Building comprehensive dashboards with interactive visualizations and real-time updates",
    "Enabling self-service analytics for business users with intuitive drag-and-drop interfaces"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Light */}
      <Header  />
     <div className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Data & <span className="text-blue-600">Analytics</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Unlock the power of your data with advanced analytics and machine learning. 
              Transform raw information into actionable insights that drive business growth 
              and competitive advantage through intelligent data processing.
            </p>
          </div>

          {/* Video Section */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white/10 backdrop-blur-sm border border-white/20">
              <video 
                className="w-full h-auto rounded-2xl" 
                autoPlay 
                muted 
                loop 
                playsInline
                src="/dataAnalytics.mp4"
                preload="metadata"
              >
                <source src="/path/to/your/video.mp4" type="video/mp4" />
                <source src="/path/to/your/video.webm" type="video/webm" />
                Your browser does not support the video tag.
              </video>
              
              {/* Optional overlay for styling */}
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/10 to-transparent pointer-events-none rounded-2xl"></div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-blue-400/20 rounded-full blur-xl"></div>
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-indigo-400/20 rounded-full blur-lg"></div>
          </div>
        </div>
      </div>
    </div>



      {/* Stats Section - Dark */}
      <div className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-400 mb-2">{animatedStats.customers.toLocaleString()}+</div>
              <div className="text-gray-300 font-medium">Active Customers</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-400 mb-2">{animatedStats.accuracy}%</div>
              <div className="text-gray-300 font-medium">Accuracy Rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-400 mb-2">{animatedStats.insights.toLocaleString()}+</div>
              <div className="text-gray-300 font-medium">Insights Generated</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-400 mb-2">{animatedStats.growth}%</div>
              <div className="text-gray-300 font-medium">Revenue Growth</div>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Statement - Light */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
       
          
          <div className="bg-blue-50 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Solution</h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              We offer a comprehensive data analytics platform that unifies disparate data sources, applies advanced 
              machine learning algorithms, and delivers actionable insights through intuitive dashboards. Our platform 
              bridges the gap between raw data and strategic decision-making.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section - Dark */}
     <div className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500/10 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-pink-500/10 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-75"></div>
        <div className="absolute bottom-20 left-40 w-72 h-72 bg-blue-500/10 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-150"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4 animate-fade-in">
            Our Data & Analytics Expertise
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed animate-fade-in delay-150">
            Our advanced Data & Analytics practice offers expert solutions that unlock the full potential of your data. 
            We specialize in transforming complex datasets into strategic business intelligence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`relative rounded-2xl p-8 shadow-2xl transition-all duration-500 group cursor-pointer transform hover:scale-105 hover:-translate-y-2 animate-slide-up`}
              style={{ animationDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Animated gradient background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${hoveredCard === index ? feature.hoverGradient : feature.gradient} rounded-2xl transition-all duration-500 animate-gradient-shift`}></div>
              
              {/* Glass overlay */}
              <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20"></div>
              
              {/* Content */}
              <div className="relative z-10 flex items-start space-x-6">
                <div className="flex-shrink-0 p-3 bg-white/20 rounded-lg group-hover:bg-white/30 transition-all duration-300 group-hover:rotate-12 group-hover:scale-110">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-gray-100 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-100 leading-relaxed group-hover:text-gray-50 transition-colors">
                    {feature.description}
                  </p>
                </div>
              </div>

              {/* Animated border */}
              <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-white/30 transition-all duration-300"></div>
            </div>
          ))}
        </div>

        <div className="relative">
          {/* Capabilities card with animated background */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-8 shadow-2xl border border-white/10 relative overflow-hidden animate-fade-in-up">
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent animate-pulse"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full animate-bounce delay-300"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full animate-bounce delay-700"></div>
            </div>

            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Key Capabilities</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {capabilities.map((capability, index) => (
                  <div 
                    key={index} 
                    className="flex items-start space-x-3 group animate-slide-in-left"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex-shrink-0 w-2 h-2 bg-yellow-400 rounded-full mt-2 group-hover:bg-yellow-300 transition-colors group-hover:scale-150 transition-transform duration-300"></div>
                    <p className="text-gray-100 leading-relaxed group-hover:text-white transition-colors">
                      {capability}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slide-in-left {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        
        .animate-slide-up {
          animation: slide-up 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-left {
          animation: slide-in-left 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
        }
        
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 3s ease infinite;
        }
      `}</style>
    </div>


<br></br>
<br></br>
<br></br>
<br></br>
<br></br>
<br></br>
    {/* <div className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover how leading organizations are transforming their data strategies with our analytics platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-100"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                  <p className="text-sm text-blue-600 font-medium">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>  */}

      {/* CTA Section - Dark */}
      <div className="py-8 bg-blue-600 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Data Strategy?</h2>
          
           <a href="/Contact" className="">
          <button className="bg-white text-blue-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-all transform hover:scale-105 shadow-lg">
            Get Started Today
            <ArrowRight className="w-5 h-5 ml-2 inline" />
          </button>
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DataAnalyticsPage;